import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import ProductReviews from '../../components/features/ProductReviews';
import RatingDisplay from '../../components/features/RatingDisplay';
import { useCurrency } from '../../context/CurrencyContext';
import { ArrowLeft, ShoppingBag, Share2 } from 'lucide-react';
import productsService from '../../services/productsService';
import logger from '../../utils/logging';
import analyticsService from '../../services/analyticsService';
import PLACEHOLDER_IMAGE from '../../utils/placeholderImage';
import { useToast } from '../../context/ToastContext';

/** De-dupe and put catalog primary first when it is not already in the gallery array */
function mergePrimaryIntoImageList(primaryImage, images) {
  const out = [];
  const seen = new Set();
  const add = (u) => {
    if (!u || seen.has(u)) return;
    seen.add(u);
    out.push(u);
  };
  if (primaryImage) add(primaryImage);
  if (Array.isArray(images)) images.forEach(add);
  return out;
}

const Product = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { showToast } = useToast();
  const { user } = useAuth();
  const { formatPrice } = useCurrency();
  const [product, setProduct] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [validImages, setValidImages] = useState([]);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxCaption, setLightboxCaption] = useState('');
  const [sharing, setSharing] = useState(false);
  const [showSizeGuide, setShowSizeGuide] = useState(false);
  const [sizeGuideUnit, setSizeGuideUnit] = useState('inches'); // 'inches' | 'cm'

  /** When set, main hero shows this URL; cleared on variant/product change so catalog primary / variant image wins */
  const [heroImageOverride, setHeroImageOverride] = useState(null);

  const openLightbox = (src, caption = '') => {
    setLightboxImage(src);
    setLightboxCaption(caption);
  };

  const closeLightbox = () => {
    setLightboxImage(null);
    setLightboxCaption('');
  };

  // Helper function to check if an image exists
  const checkImageExists = async (imagePath) => {
    try {
      const response = await fetch(imagePath, { method: 'HEAD' });
      return response.ok;
    } catch {
      return false;
    }
  };

  useEffect(() => {
    const loadProduct = async () => {
      try {
        logger.logComponentMount('Product', { slug });

        if (!slug) {
          setLoading(false);
          return;
        }

        // Try to fetch product directly by ID first (if slug is a UUID)
        let foundProduct = null;
        const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(slug);

        if (isUUID) {
          foundProduct = await productsService.getProduct(slug);
        }

        if (!foundProduct) {
          const allProducts = await productsService.getProducts();
          foundProduct = allProducts.find(p => {
            const productSlug = p.slug || p.name.toLowerCase().replace(/\s+/g, '-');
            return productSlug === slug || p.id === slug;
          });

          if (foundProduct) {
            const fullProduct = await productsService.getProduct(foundProduct.id);
            if (fullProduct) {
              setProduct(fullProduct);
              logger.logProductView(fullProduct.id, fullProduct.name, { category: fullProduct.category, price: fullProduct.price });
              analyticsService.trackProductView(fullProduct);
            } else {
              setProduct(foundProduct);
              logger.logProductView(foundProduct.id, foundProduct.name, { category: foundProduct.category, price: foundProduct.price });
              analyticsService.trackProductView(foundProduct);
            }
          }
        } else {
          setProduct(foundProduct);
          logger.logProductView(foundProduct.id, foundProduct.name, { category: foundProduct.category, price: foundProduct.price });
          analyticsService.trackProductView(foundProduct);
        }

        if (!foundProduct) {
          setLoading(false);
          return;
        }
      } catch (error) {
        logger.logError(error, 'Product component');
        setLoading(false);
        return;
      } finally {
        setLoading(false);
      }
    };

    loadProduct();
  }, [slug, navigate]);

  // Auto-select first variant when there's only one
  useEffect(() => {
    if (product?.variants?.length === 1 && selectedVariant === null) {
      setSelectedVariant(0);
      const v = product.variants[0];
      if (v.color) setSelectedColor(v.color);
      if (v.size) setSelectedSize(v.size);
    }
  }, [product?.variants, selectedVariant]);

  // When product changes, reset color/size selection
  useEffect(() => {
    setSelectedColor(null);
    setSelectedSize(null);
    setSelectedVariant(null);
  }, [product?.id]);

  // Resolve selectedVariant index from selectedColor + selectedSize
  useEffect(() => {
    if (!product?.variants?.length) return;
    const variants = product.variants;
    const hasColorOptions = variants.some(v => v.color);
    const hasSizeOptions = variants.some(v => v.size);

    if (!hasColorOptions && !hasSizeOptions) return; // flat list handled separately

    const idx = variants.findIndex(v => {
      const colorMatch = !hasColorOptions || selectedColor === null || v.color === selectedColor;
      const sizeMatch = !hasSizeOptions || selectedSize === null || v.size === selectedSize;
      return colorMatch && sizeMatch;
    });
    setSelectedVariant(idx >= 0 ? idx : null);
  }, [selectedColor, selectedSize, product?.variants]);

  useEffect(() => {
    setHeroImageOverride(null);
  }, [product?.id, selectedVariant]);

  // Load related products when product is set (product?.id and product?.category are sufficient deps)
  useEffect(() => {
    if (!product) return;
    let cancelled = false;
    const category = product.category;
    const productId = product.id;
    productsService.getProducts()
      .then((all) => {
        if (cancelled) return;
        const related = all
          .filter((p) => p.id !== productId && p.isActive !== false)
          .filter((p) => !category || p.category === category)
          .slice(0, 4);
        setRelatedProducts(related.length ? related : all.filter((p) => p.id !== productId).slice(0, 4));
      })
      .catch(() => setRelatedProducts([]));
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- product?.id and product?.category are sufficient; full product would cause extra fetches
  }, [product?.id, product?.category]);

  // Sale-aware price: use salePrice when on sale, else base price
  const getDisplayPrice = () => {
    if (!product) {
      return { price: 0, original: 0, onSale: false };
    }
    if (product.variants && product.variants.length > 0 && selectedVariant !== null) {
      const v = product.variants[selectedVariant];
      const vsp = product.variantSalePrices?.[v?.id];
      if (vsp?.saleInfo) return { price: vsp.salePrice, original: vsp.originalPrice, onSale: true };
      const p = typeof v?.price === 'string' ? parseFloat(String(v?.price || 0).replace(/[^0-9.]/g, '')) : parseFloat(v?.price || 0);
      return { price: p, original: p, onSale: false };
    }
    if (product.variants && product.variants.length > 0) {
      const prices = product.variants.map((v) => {
        const vsp = product.variantSalePrices?.[v.id];
        if (vsp?.saleInfo) return vsp.salePrice;
        return typeof v.price === 'string' ? parseFloat(String(v.price || 0).replace(/[^0-9.]/g, '')) : parseFloat(v.price || 0);
      });
      const minP = Math.min(...prices);
      const maxP = Math.max(...prices);
      return { price: minP, priceRange: [minP, maxP], original: null, onSale: prices.some((_, i) => product.variantSalePrices?.[product.variants[i]?.id]?.saleInfo) };
    }
    if (product.isOnSale && product.salePrice != null) {
      return { price: product.salePrice, original: product.originalPrice ?? product.price, onSale: true };
    }
    const p = parseFloat(product.price || 0);
    return { price: p, original: p, onSale: false };
  };

  const getEffectivePriceForCart = (variantOrProduct) => {
    if (!product) return 0;
    if (variantOrProduct?.price != null && product.variantSalePrices?.[variantOrProduct?.id]?.saleInfo) {
      return product.variantSalePrices[variantOrProduct.id].salePrice;
    }
    if (product.isOnSale && product.salePrice != null && !variantOrProduct) return product.salePrice;
    const raw = variantOrProduct?.price ?? product.price;
    return typeof raw === 'string' ? parseFloat(String(raw).replace(/[^0-9.]/g, '')) : parseFloat(raw || 0);
  };

  const displayPrice = getDisplayPrice();
  const saleInfo =
    product &&
    product.variants?.[selectedVariant] != null &&
    product.variantSalePrices?.[product.variants[selectedVariant].id]?.saleInfo
      ? product.variantSalePrices[product.variants[selectedVariant].id].saleInfo
      : product?.saleInfo ?? null;

  const handleShare = async () => {
    setSharing(true);
    const url = typeof window !== 'undefined' ? window.location.href : '';
    const text = product ? `Check out ${product.name} on Rainbow Sails!` : 'Check out Rainbow Sails!';
    try {
      if (navigator.share) {
        await navigator.share({ title: product?.name || 'Rainbow Sails', text, url });
        showToast('Shared with friends!', 'success');
      } else {
        await navigator.clipboard.writeText(url);
        showToast('Link copied! Share with your friends.', 'success');
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        await navigator.clipboard.writeText(url).catch(() => {});
        showToast('Link copied! Share with your friends.', 'success');
      }
    } finally {
      setSharing(false);
    }
  };

  // Validate images when product changes
  useEffect(() => {
    const validateImages = async () => {
      if (!product) return;

      const imagesToCheck = [];

      // For Mango products, use images array directly (already full URLs)
      if (product.images && Array.isArray(product.images) && product.images.length > 0) {
        product.images.forEach((imageUrl, index) => {
          imagesToCheck.push({
            src: imageUrl,
            label: index === 0 ? 'Primary Image' : `Image ${index + 1}`,
            type: index === 0 ? 'primary' : 'gallery',
            alt: `${product.name} - ${index === 0 ? 'Primary' : `Image ${index + 1}`}`,
            localPath: imageUrl // Already full URL from productsService
          });
        });
      }
      
      // Only check folder-based images if folder exists (legacy products)
      if (!product.folder) {
        setValidImages(imagesToCheck);
        return;
      }

      // Legacy folder-based system (for old products)
      const mainCustomImagePath = product.folder ? `/products/${product.folder}/custom-0.jpg` : null;
      if (mainCustomImagePath) {
        imagesToCheck.push({
          src: 'custom-0.jpg',
          label: 'Main Design',
          type: 'main-custom',
          alt: `${product.name} - Main Design`,
          localPath: mainCustomImagePath
        });
      }
      
      // Add custom images
      if (product.customImages && product.customImages.length > 0) {
        product.customImages.forEach((imageName, index) => {
          imagesToCheck.push({
            src: imageName,
            label: `Design ${index + 1}`,
            type: 'custom',
            alt: `${product.name} - Custom ${index + 1}`,
            localPath: `/products/${product.folder}/${imageName}`
          });
        });
      }
      
      // Add variant images using correct naming pattern (variant-0, variant-1, etc.)
      if (product.variants && product.variants.length > 0) {
        product.variants.forEach((variant, index) => {
          // Add preview image for each variant (all preview images)
          const previewImageName = `variant-${index}-preview-1.png`;
          imagesToCheck.push({
            src: previewImageName,
            label: `${variant.size || variant.name || `Variant ${index + 1}`} Preview`,
            type: 'variant-preview',
            alt: `${product.name} - ${variant.size || variant.name} Preview`,
            localPath: `/products/${product.folder}/${previewImageName}`
          });
        });
        
        // Add ONLY the first variant's default image (not all variants)
        if (product.variants.length > 0) {
          const firstVariantDefaultImageName = `variant-0-default-0.png`;
          imagesToCheck.push({
            src: firstVariantDefaultImageName,
            label: `${product.variants[0].size || product.variants[0].name || 'Variant 1'} Default`,
            type: 'variant-default',
            alt: `${product.name} - ${product.variants[0].size || product.variants[0].name} Default`,
            localPath: `/products/${product.folder}/${firstVariantDefaultImageName}`
          });
        }
      }
      
      // Add any additional gallery images if they exist
      if (product.galleryImages && Array.isArray(product.galleryImages)) {
        product.galleryImages.forEach((imageItem, index) => {
          // Handle both string filenames and image objects
          const imageName = typeof imageItem === 'string' ? imageItem : (imageItem.filename || imageItem.name || imageItem.url);
          if (imageName) {
            imagesToCheck.push({
              src: imageName,
              label: `Gallery ${index + 1}`,
              type: 'gallery',
              alt: `${product.name} - Gallery ${index + 1}`,
              localPath: `/products/${product.folder}/${imageName}`
            });
          }
        });
      }
      
      // Check which images actually exist
      const validImagesList = [];
      for (const image of imagesToCheck) {
        const exists = await checkImageExists(image.localPath);
        if (exists) validImagesList.push(image);
      }
      setValidImages(validImagesList);
    };
    
    validateImages();
  }, [product]);

  useEffect(() => {
    if (!lightboxImage) {
      return;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setLightboxImage(null);
        setLightboxCaption('');
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [lightboxImage]);

  const handleSubscriptionCheckout = async () => {
    if (!user) {
      showToast('Please create a profile and log in to subscribe.', 'error');
      navigate('/auth?redirect=/product/mystery-test-subscription');
      return;
    }

    if (!user.email) {
      showToast('Please complete your profile with an email address to subscribe.', 'error');
      navigate('/profile');
      return;
    }

    setSubscriptionLoading(true);
    try {
      const isProduction = typeof window !== 'undefined' && 
        (window.location.hostname.includes('rainbow-sails.shop') ||
         (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1'));
      const subscriptionApiUrl = process.env.REACT_APP_API_URL || 
        (isProduction ? 'https://api.rainbow-sails.shop/api/v1' : 'http://localhost:4000/api/v1');
      const baseUrl = subscriptionApiUrl.replace('/api/v1', '');
      const response = await fetch(`${baseUrl}/api/subscriptions/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: user.id,
          customerEmail: user.email,
          customerName: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email,
        }),
      });

      const data = await response.json();

      if (data.success && data.url) {
        // Redirect to Stripe checkout
        window.location.href = data.url;
      } else {
        showToast(data.error || 'Failed to create subscription checkout. Please try again.', 'error');
        setSubscriptionLoading(false);
      }
    } catch (error) {
      console.error('❌ Subscription checkout error:', error);
      showToast('An error occurred. Please try again.', 'error');
      setSubscriptionLoading(false);
    }
  };

  const handleAddToCart = () => {
    // Handle subscription separately
    if (product && (product.subscription || product.type === 'subscription')) {
      handleSubscriptionCheckout();
      return;
    }

    // Check if product has variants with length > 0 AND a variant is selected
    if (product && product.variants && product.variants.length > 0 && product.variants[selectedVariant]) {
      const variant = product.variants[selectedVariant];
      logger.logButtonClick('Add to Cart', { 
        productId: product.id, 
        productName: product.name,
        variant: variant.name || variant.size,
        price: variant.price
      });
      logger.logCartAction('add_item', product.id, product.name, {
        variant: variant.name || variant.size,
        price: variant.price
      });
      // Get the correct image URL - use custom image for cart display
      const imageUrl = product.folder 
        ? `/products/${product.folder}/custom-0.jpg`
        : (product.customImage || PLACEHOLDER_IMAGE);
      
      const cartItem = {
        id: `${product.id}-${variant.id}`,
        title: [product.name, variant.color, variant.size].filter(Boolean).join(' / '),
        image: imageUrl,  // ← Use the correct image from galleryImages
        price: getEffectivePriceForCart(variant),
        variant: variant.color || variant.size,
        size: variant.size,
        type: 'product',
        // Store correct IDs for order processing
        productId: product.id,
        variantId: variant.variantId,
        printfulVariantId: variant.printfulVariantId,
        printfulProductId: product.printfulData?.printfulProductId,
        // Add Printful order data for checkout
        printful_data: {
          variant_id: variant.printfulVariantId,
          files: [{
            type: "front",
            url: variant.order_data?.files?.[0]?.url || product.variants?.[0]?.order_data?.files?.[0]?.url || "No image available"
          }]
        }
      };
      for (let q = 0; q < quantity; q++) addToCart(cartItem);
      showToast(`Added to cart${quantity > 1 ? ` (×${quantity})` : ''}!`, 'success');
    } else if (product && (!product.variants || product.variants.length === 0)) {
      // Product has no variants or empty variants array - add the basic product
      logger.logButtonClick('Add to Cart', { 
        productId: product.id, 
        productName: product.name,
        price: product.price
      });
      logger.logCartAction('add_item', product.id, product.name, {
        price: product.price
      });
      
      // Get product image - check multiple sources
      const imageUrl = product.primaryImage || 
                       product.customImage || 
                       (product.images && product.images.length > 0 ? product.images[0] : null) ||
                       (product.folder ? `/products/${product.folder}/custom-0.jpg` : PLACEHOLDER_IMAGE);
      
      const cartItem = {
        id: product.id,
        title: product.name,
        image: imageUrl,
        price: getEffectivePriceForCart(null),
        variant: 'default',
        size: 'default',
        type: 'product',
        // Store correct IDs for order processing
        productId: product.id,
        variantId: product.id,
        printfulVariantId: null,
        printfulProductId: product.printfulData?.printfulProductId
      };
      for (let q = 0; q < quantity; q++) addToCart(cartItem);
      showToast(`Added to cart${quantity > 1 ? ` (×${quantity})` : ''}!`, 'success');
    } else {
      const hasColors = product.variants.some(v => v.color);
      const hasSizes  = product.variants.some(v => v.size);
      const missing = [
        hasColors && !selectedColor && 'colour',
        hasSizes  && !selectedSize  && 'size',
      ].filter(Boolean);
      showToast(
        missing.length > 0
          ? `Please select a ${missing.join(' and ')} before adding to cart`
          : 'Please select a style before adding to cart',
        'error'
      );
    }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'radial-gradient(ellipse at center, #1a1a2e 0%, #16213e 50%, #0f0f23 100%)',
        paddingTop: '80px'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '32px 24px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '48px', alignItems: 'start' }}>
            <div>
              <div style={{ width: '100%', aspectRatio: '1', background: 'rgba(255,255,255,0.08)', borderRadius: '16px', animation: 'productSkeletonPulse 1.5s ease-in-out infinite' }} />
            </div>
            <div>
              <div style={{ height: 48, background: 'rgba(255,255,255,0.08)', borderRadius: '8px', marginBottom: 16, width: '80%', animation: 'productSkeletonPulse 1.5s ease-in-out infinite' }} />
              <div style={{ height: 24, background: 'rgba(255,255,255,0.08)', borderRadius: '8px', marginBottom: 24, width: '50%', animation: 'productSkeletonPulse 1.5s ease-in-out infinite' }} />
              <div style={{ height: 96, background: 'rgba(255,255,255,0.08)', borderRadius: '12px', marginBottom: 24, animation: 'productSkeletonPulse 1.5s ease-in-out infinite' }} />
              <div style={{ height: 56, background: 'rgba(255,255,255,0.08)', borderRadius: '12px', width: '100%', animation: 'productSkeletonPulse 1.5s ease-in-out infinite' }} />
            </div>
          </div>
        </div>
        <style>{`@keyframes productSkeletonPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }`}</style>
      </div>
    );
  }

  if (!product) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'radial-gradient(ellipse at center, #1a1a2e 0%, #16213e 50%, #0f0f23 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        paddingTop: '80px'
      }}>
        <div style={{ textAlign: 'center', color: 'white' }}>
          <p style={{ fontSize: '18px', marginBottom: '16px' }}>Product not found</p>
          <button
            onClick={() => navigate('/gallery')}
            style={{
              background: 'linear-gradient(135deg, #ec4899, #8b5cf6)',
              color: 'white',
              padding: '12px 24px',
              borderRadius: '8px',
              border: 'none',
              cursor: 'pointer'
            }}
          >
            Back to Gallery
          </button>
        </div>
      </div>
    );
  }

  const currentVariant = product?.variants?.[selectedVariant] || null;

  const mainImageSrc = (() => {
    // Priority: primaryImage > images[0] > customImage > variant image > Printful > placeholder
    if (selectedVariant !== null && product.variants && product.variants[selectedVariant]) {
      const variant = product.variants[selectedVariant];
      // Check variant-specific image first
      if (variant.image) return variant.image;
      // Then check product folder (legacy support)
      if (product.folder) {
        return `/products/${product.folder}/${variant.previewImage || variant.defaultImage}`;
      }
    }
    
    // Use primaryImage (already full URL from productsService)
    if (product.primaryImage) return product.primaryImage;
    
    // Use first image from images array (already full URLs from productsService)
    if (product.images && product.images.length > 0) return product.images[0];
    
    // Use customImage (already full URL from productsService)
    if (product.customImage) return product.customImage;
    
    // Legacy folder support
    if (product.folder) {
      return `/products/${product.folder}/custom-0.jpg`;
    }
    
    // Printful fallback
    if (product.printfulData?.designThumbnailUrl) {
      return product.printfulData.designThumbnailUrl;
    }
    
    // Final fallback to placeholder constant
    return PLACEHOLDER_IMAGE;
  })();

  const displayHeroSrc = heroImageOverride ?? mainImageSrc;

  const storeUrl = process.env.REACT_APP_STORE_URL || (typeof window !== 'undefined' ? window.location.origin : 'https://rainbow-sails.shop');
  const productSlug = product.slug || product.name?.toLowerCase().replace(/\s+/g, '-') || slug;
  const productUrl = `${storeUrl}/product/${productSlug}`;
  const ogImage = mainImageSrc?.startsWith('http') ? mainImageSrc : (mainImageSrc ? `${storeUrl}${mainImageSrc.startsWith('/') ? '' : '/'}${mainImageSrc}` : null);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'radial-gradient(ellipse at center, #1a1a2e 0%, #16213e 50%, #0f0f23 100%)',
      paddingTop: '80px'
    }}>
      <Helmet>
        <title>{product.name} | Rainbow Sails</title>
        <meta name="description" content={product.shortDescription || product.description?.slice(0, 160) || product.name} />
        <meta property="og:title" content={`${product.name} | Rainbow Sails`} />
        <meta property="og:description" content={product.shortDescription || product.description?.slice(0, 160) || product.name} />
        <meta property="og:url" content={productUrl} />
        {ogImage && <meta property="og:image" content={ogImage} />}
        <link rel="canonical" href={productUrl} />
      </Helmet>
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: window.innerWidth <= 768 ? '32px 16px' : '32px 24px',
        width: '100%',
        boxSizing: 'border-box',
        position: 'relative',
        zIndex: 10
      }}>
        {/* Back Button - use history when available */}
        <button
          onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/gallery'))}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: 'rgba(255, 255, 255, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            color: 'white',
            padding: '12px 16px',
            borderRadius: '8px',
            cursor: 'pointer',
            marginBottom: '32px',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.background = 'rgba(255, 255, 255, 0.2)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'rgba(255, 255, 255, 0.1)';
          }}
        >
          <ArrowLeft size={20} />
          Back to Gallery
        </button>

        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '48px',
          alignItems: 'start'
        }}>
          {/* Product Images */}
          <div>
            {/* Main Product Image */}
            <div style={{ marginBottom: '24px' }}>
              {product.subscription || product.type === 'subscription' ? (
                <div style={{
                  width: '100%',
                  minHeight: '400px',
                  background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.4), rgba(236, 72, 153, 0.4))',
                  borderRadius: '16px',
                  boxShadow: '0 20px 40px rgba(139, 92, 246, 0.5)',
                  border: '2px solid rgba(139, 92, 246, 0.6)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  <img
                    src="/favicon.png"
                    alt="Rainbow Sails Subscription"
                    style={{
                      width: '200px',
                      height: '200px',
                      objectFit: 'contain',
                      animation: 'pulse 2s ease-in-out infinite',
                      filter: 'drop-shadow(0 0 30px rgba(255, 255, 255, 0.6))',
                      marginBottom: '20px'
                    }}
                    onError={(e) => {
                      e.target.src = '/Logo.png';
                    }}
                  />
                  <div style={{
                    background: 'rgba(0, 0, 0, 0.7)',
                    color: 'white',
                    padding: '12px 24px',
                    borderRadius: '12px',
                    fontSize: '18px',
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    letterSpacing: '2px'
                  }}>
                    Zee Mystery Subscription Test
                  </div>
                  <div style={{
                    position: 'absolute',
                    bottom: '20px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    color: '#e9d5ff',
                    fontSize: '14px',
                    fontStyle: 'italic',
                    textAlign: 'center',
                    padding: '0 20px'
                  }}>
                    The secret stays sealed until Zee drops the next test surprise!
                  </div>
                </div>
              ) : (
                <>
                  <img
                    src={displayHeroSrc}
                    alt={product.name}
                    data-product-hero="true"
                    role="button"
                    tabIndex={0}
                    style={{
                      width: '100%',
                      height: 'auto',
                      borderRadius: '16px',
                      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.5)',
                      border: '2px solid rgba(16, 185, 129, 0.3)',
                      cursor: 'zoom-in'
                    }}
                    onClick={() => openLightbox(displayHeroSrc, `${product.name} • Zee full view`)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        openLightbox(displayHeroSrc, `${product.name} • Zee full view`);
                      }
                    }}
                    onError={(e) => {
                      console.error('❌ [IMAGE ERROR] Failed to load image:', e.target.src);
                      // Fallback chain for Mango products (using images array)
                      if (product.images && product.images.length > 0) {
                        const currentIndex = product.images.indexOf(e.target.src);
                        if (currentIndex !== -1 && currentIndex < product.images.length - 1) {
                          const next = product.images[currentIndex + 1];
                          e.target.src = next;
                          setHeroImageOverride(next);
                          return;
                        }
                      }
                      
                      // Fallback for legacy folder-based products
                      if (product.folder) {
                        if (e.target.src.includes('preview')) {
                          const variant = product.variants?.[selectedVariant];
                          if (variant && variant.defaultImage) {
                            e.target.src = `/products/${product.folder}/${variant.defaultImage}`;
                            return;
                          } else {
                            e.target.src = `/products/${product.folder}/custom-0.jpg`;
                            return;
                          }
                        } else if (e.target.src.includes('custom-0.jpg')) {
                          e.target.src = `/products/${product.folder}/variant-0-default-0.png`;
                          return;
                        } else if (e.target.src.includes('variant-0-default-0.png')) {
                          e.target.src = product.printfulData?.designThumbnailUrl || PLACEHOLDER_IMAGE;
                          return;
                        }
                      }
                      
                      // Final fallbacks
                      if (product.printfulData?.designThumbnailUrl) {
                        e.target.src = product.printfulData.designThumbnailUrl;
                        return;
                      }
                      
                      // Use placeholder constant (not '/placeholder.jpg')
                      if (!e.target.src.startsWith('data:') && e.target.src !== PLACEHOLDER_IMAGE) {
                        e.target.src = PLACEHOLDER_IMAGE;
                      }
                    }}
                  />
                  <p style={{
                    textAlign: 'center',
                    color: '#10b981',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    marginTop: '8px'
                  }}>
                    {selectedVariant !== null && currentVariant?.size
                      ? currentVariant.size
                      : (product.variants?.length > 0
                        ? (product.variants.some(v => v.size || v.color) ? 'Select colour & size above' : 'Select a style above')
                        : product.size_info?.material)
                    } • {product.size_info?.material || 'Premium Quality'}
                  </p>
                </>
              )}
            </div>

            {/* Complete Image Gallery - Show all available images */}
            {((product.images && product.images.length > 1) || validImages.length > 0) && (
              <div style={{ marginTop: '24px' }}>
                <h3 style={{
                  color: 'white',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  marginBottom: '16px',
                  textAlign: 'center'
                }}>
                  Product Gallery
                </h3>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                  gap: '12px',
                  maxHeight: '400px',
                  overflowY: 'auto',
                  padding: '8px',
                  background: 'rgba(0, 0, 0, 0.3)',
                  borderRadius: '12px',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                  {(() => {
                    let imagesToDisplay = [];

                    if (product.images && Array.isArray(product.images) && product.images.length > 1) {
                      const urls = mergePrimaryIntoImageList(product.primaryImage, product.images);
                      imagesToDisplay = urls.map((imageUrl, index) => {
                        const meta = Array.isArray(product.galleryImages)
                          ? product.galleryImages.find((g) => g && g.url === imageUrl)
                          : null;
                        const isPrimary = !!(
                          meta?.isPrimary ||
                          (product.primaryImage && imageUrl === product.primaryImage)
                        );
                        return {
                          src: imageUrl,
                          label: isPrimary ? 'Primary' : `Image ${index + 1}`,
                          type: isPrimary ? 'primary' : 'gallery',
                          alt: `${product.name} - ${isPrimary ? 'Primary' : `Image ${index + 1}`}`,
                          localPath: imageUrl,
                          isPrimary,
                        };
                      });
                    } else if (validImages.length > 0) {
                      imagesToDisplay = validImages.map((img, index) => ({
                        ...img,
                        isPrimary: img.type === 'primary' || img.type === 'main-custom' || index === 0,
                      }));
                    }

                    if (imagesToDisplay.length === 0) {
                      return (
                        <div style={{
                          textAlign: 'center',
                          color: '#9ca3af',
                          padding: '20px',
                          fontStyle: 'italic'
                        }}>
                          No additional images available
                        </div>
                      );
                    }

                    return imagesToDisplay.map((image, index) => {
                      const isActive = displayHeroSrc === image.localPath;
                      return (
                        <div key={`${image.type}-${index}-${image.localPath}`} style={{ position: 'relative' }}>
                          <img
                            src={image.localPath}
                            alt={image.alt}
                            role="button"
                            tabIndex={0}
                            aria-label={image.alt}
                            aria-current={isActive ? 'true' : undefined}
                            style={{
                              width: '100%',
                              height: '120px',
                              objectFit: 'cover',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              border: isActive ? '2px solid #10b981' : '2px solid transparent',
                              boxShadow: isActive ? '0 0 0 2px rgba(16, 185, 129, 0.35)' : 'none',
                              outline: 'none',
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                            onMouseEnter={(e) => {
                              if (!isActive) {
                                e.target.style.transform = 'scale(1.05)';
                                e.target.style.borderColor = 'rgba(16, 185, 129, 0.6)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.transform = 'scale(1)';
                              if (!isActive) e.target.style.borderColor = 'transparent';
                            }}
                            onClick={() => setHeroImageOverride(image.localPath)}
                            onKeyDown={(ev) => {
                              if (ev.key === 'Enter' || ev.key === ' ') {
                                ev.preventDefault();
                                setHeroImageOverride(image.localPath);
                              }
                            }}
                          />
                          {image.isPrimary && (
                            <span style={{
                              position: 'absolute',
                              bottom: '6px',
                              left: '6px',
                              fontSize: '10px',
                              fontWeight: 700,
                              textTransform: 'uppercase',
                              letterSpacing: '0.04em',
                              color: '#fff',
                              background: 'rgba(16, 185, 129, 0.92)',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              pointerEvents: 'none',
                            }}>
                              Primary
                            </span>
                          )}
                        </div>
                      );
                    });
                  })()}
                </div>
                <p style={{
                  textAlign: 'center',
                  color: '#9ca3af',
                  fontSize: '12px',
                  marginTop: '8px',
                  fontStyle: 'italic'
                }}>
                  Tap a thumbnail to preview; the one with the green ring matches the large image. “Primary” is the catalog cover photo.
                </p>
              </div>
            )}
          </div>

          {/* Product Details */}
          <div>
            <div style={{ marginBottom: '24px' }}>
              <h1 style={{
                fontSize: '48px',
                fontWeight: 'bold',
                marginBottom: '12px',
                textAlign: 'center',
                background: 'linear-gradient(45deg, #ff0080, #ff8000, #ffff00, #80ff00, #00ff80, #00ffff, #0080ff, #8000ff, #ff0080)',
                backgroundSize: '400% 400%',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: 'transparent',
                animation: 'rainbowDrip 4s ease-in-out infinite, textGlow 2s ease-in-out infinite alternate',
                textShadow: '0 0 30px rgba(255, 0, 128, 0.8), 0 0 60px rgba(255, 128, 0, 0.6), 0 0 90px rgba(255, 255, 0, 0.4)',
                fontFamily: 'Arial, sans-serif',
                letterSpacing: '2px',
                transform: 'perspective(500px) rotateX(15deg)',
                filter: 'drop-shadow(0 0 20px rgba(255, 0, 128, 0.5))'
              }}>
                {product.name}
              </h1>
              
              {/* Rating Display */}
              <div style={{ marginBottom: '16px' }}>
                <RatingDisplay productId={product.id} size="large" showCount={true} />
              </div>
              
              {(product.artist || product.brand) && (
                <p style={{
                  color: '#9ca3af',
                  fontSize: '18px',
                  marginBottom: '8px'
                }}>
                  {product.artist && product.brand 
                    ? `by ${product.artist} • ${product.brand}`
                    : product.artist 
                      ? `by ${product.artist}`
                      : product.brand
                        ? product.brand
                        : ''}
                </p>
              )}
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <div style={{
                  background: 'rgba(59, 130, 246, 0.1)',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  border: '1px solid rgba(59, 130, 246, 0.2)',
                  display: 'inline-block'
                }}>
                  <span style={{ color: '#3b82f6', fontSize: '14px', fontWeight: '500' }}>
                    {product.category} • {product.technique}
                  </span>
                </div>
                <button
                  onClick={handleShare}
                  disabled={sharing}
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    padding: '10px 16px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    cursor: sharing ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  title="Share with friends"
                  aria-label="Share product with friends"
                >
                  <Share2 size={16} />
                  {sharing ? 'Sharing...' : 'Share'}
                </button>
              </div>
            </div>

            {/* Short Description */}
            {product.shortDescription && (
              <div style={{ marginBottom: '24px' }}>
                <div style={{
                  color: '#d1d5db',
                  fontSize: '18px',
                  lineHeight: '1.6',
                  fontWeight: '500',
                  background: 'rgba(236, 72, 153, 0.1)',
                  padding: '16px',
                  borderRadius: '8px',
                  border: '1px solid rgba(236, 72, 153, 0.2)',
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word'
                }}>
                  {product.shortDescription}
                </div>
              </div>
            )}

            {(product.subscription || product.type === 'subscription') && (
              <div style={{
                marginBottom: '24px',
                background: 'rgba(59, 130, 246, 0.12)',
                border: '1px solid rgba(59, 130, 246, 0.35)',
                borderRadius: '10px',
                padding: '16px'
              }}>
                <p style={{
                  color: '#bfdbfe',
                  fontSize: '16px',
                  lineHeight: '1.6',
                  margin: '0 0 8px 0'
                }}>
                  ℹ️ Zee\'s reminder: auto-renew starts off. After checkout, open Profile &gt; Subscription and enable auto-renew if you want to stay in the $1 mystery subscription test.
                </p>
                <p style={{
                  color: '#cbd5f5',
                  fontSize: '15px',
                  lineHeight: '1.6',
                  margin: 0
                }}>
                  Testers who stick with Zee will be first to receive the mystery drop when the experiment wraps.
                </p>
              </div>
            )}

            {/* Full Description */}
            {product.description && product.description !== product.shortDescription && (
              <div style={{ marginBottom: '32px' }}>
                <h3 style={{
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  marginBottom: '12px'
                }}>
                  Product Details
                </h3>
                <div style={{
                  color: '#9ca3af',
                  fontSize: '16px',
                  lineHeight: '1.6',
                  fontStyle: 'italic',
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word'
                }}>
                  {product.description}
                </div>
              </div>
            )}

            {/* Variant Selection — grouped Color + Size, or flat list for name-only variants */}
            {product.variants && product.variants.length > 0 && (() => {
              const variants = product.variants;
              const uniqueColors = [...new Set(variants.map(v => v.color).filter(Boolean))];
              const uniqueSizes  = [...new Set(variants.map(v => v.size).filter(Boolean))];
              const hasColors = uniqueColors.length > 0;
              const hasSizes  = uniqueSizes.length > 0;

              // Sizes available for the currently-selected color (cross-filter)
              const sizesForColor = hasColors && selectedColor
                ? [...new Set(variants.filter(v => v.color === selectedColor).map(v => v.size).filter(Boolean))]
                : uniqueSizes;

              // Colors available for the currently-selected size (cross-filter)
              const colorsForSize = hasSizes && selectedSize
                ? [...new Set(variants.filter(v => v.size === selectedSize).map(v => v.color).filter(Boolean))]
                : uniqueColors;

              if (!hasColors && !hasSizes) {
                // Fallback: flat list of name-based variants (unchanged behaviour)
                if (variants.length <= 1) return null;
                return (
                  <div style={{ marginBottom: '24px' }}>
                    <h3 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '12px', color: 'white' }}>
                      Choose Your Style:
                    </h3>
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                      {variants.map((variant, index) => {
                        const label = variant.name?.includes('/') ? variant.name.split('/')[1]?.trim() : variant.name;
                        const isSelected = selectedVariant === index;
                        return (
                          <button
                            key={index}
                            type="button"
                            onClick={() => setSelectedVariant(index)}
                            style={{
                              padding: '10px 16px',
                              borderRadius: '8px',
                              border: isSelected ? '2px solid #ec4899' : '1px solid rgba(255,255,255,0.2)',
                              background: isSelected ? 'rgba(236,72,153,0.2)' : 'rgba(255,255,255,0.1)',
                              color: 'white',
                              cursor: 'pointer',
                              fontSize: '14px',
                              transition: 'all 0.2s',
                            }}
                          >
                            {label || variant.name}
                            <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
                              {product.variantSalePrices?.[variant.id]?.saleInfo
                                ? <span style={{ color: '#10b981' }}>{formatPrice(product.variantSalePrices[variant.id].salePrice)}</span>
                                : formatPrice(variant.price)
                              }
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              }

              return (
                <div style={{ marginBottom: '24px' }}>
                  {/* Color selector */}
                  {hasColors && (
                    <div style={{ marginBottom: '18px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                        <span style={{ color: '#9ca3af', fontSize: '13px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Color</span>
                        {selectedColor && (
                          <span style={{ color: 'white', fontSize: '14px', fontWeight: '500' }}>{selectedColor}</span>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                        {uniqueColors.map(color => {
                          const isSelected = selectedColor === color;
                          const isAvailable = !hasSizes || colorsForSize.includes(color);
                          return (
                            <button
                              key={color}
                              type="button"
                              onClick={() => {
                                setSelectedColor(isSelected ? null : color);
                                // Clear size if the currently-selected size is unavailable for new color
                                if (!isSelected && selectedSize) {
                                  const sizesForNewColor = variants.filter(v => v.color === color).map(v => v.size).filter(Boolean);
                                  if (!sizesForNewColor.includes(selectedSize)) setSelectedSize(null);
                                }
                              }}
                              title={color}
                              style={{
                                padding: '8px 18px',
                                borderRadius: '20px',
                                border: isSelected ? '2px solid #ec4899' : '1px solid rgba(255,255,255,0.25)',
                                background: isSelected ? 'rgba(236,72,153,0.25)' : 'rgba(255,255,255,0.08)',
                                color: isAvailable ? 'white' : '#4b5563',
                                cursor: isAvailable ? 'pointer' : 'default',
                                fontSize: '14px',
                                fontWeight: isSelected ? '600' : '400',
                                transition: 'all 0.2s',
                                opacity: isAvailable ? 1 : 0.45,
                                boxShadow: isSelected ? '0 0 0 1px #ec4899' : 'none',
                              }}
                            >
                              {color}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Size selector */}
                  {hasSizes && (
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ color: '#9ca3af', fontSize: '13px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Size</span>
                          {selectedSize && (
                            <span style={{ color: 'white', fontSize: '14px', fontWeight: '500' }}>{selectedSize}</span>
                          )}
                        </div>
                        {product.sizeGuide && (
                          <button
                            type="button"
                            onClick={() => setShowSizeGuide(true)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#a78bfa',
                              fontSize: '12px',
                              fontWeight: '500',
                              cursor: 'pointer',
                              textDecoration: 'underline',
                              padding: 0,
                            }}
                          >
                            Size Guide
                          </button>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                        {uniqueSizes.map(size => {
                          const isSelected = selectedSize === size;
                          const isAvailable = !hasColors || sizesForColor.includes(size);
                          return (
                            <button
                              key={size}
                              type="button"
                              onClick={() => isAvailable && setSelectedSize(isSelected ? null : size)}
                              style={{
                                width: '52px',
                                height: '52px',
                                borderRadius: '8px',
                                border: isSelected ? '2px solid #8b5cf6' : '1px solid rgba(255,255,255,0.25)',
                                background: isSelected ? 'rgba(139,92,246,0.25)' : 'rgba(255,255,255,0.08)',
                                color: isAvailable ? 'white' : '#4b5563',
                                cursor: isAvailable ? 'pointer' : 'not-allowed',
                                fontSize: '13px',
                                fontWeight: isSelected ? '700' : '400',
                                transition: 'all 0.2s',
                                opacity: isAvailable ? 1 : 0.4,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                position: 'relative',
                                boxShadow: isSelected ? '0 0 0 1px #8b5cf6' : 'none',
                              }}
                            >
                              {!isAvailable && (
                                <span style={{
                                  position: 'absolute',
                                  top: '50%',
                                  left: '50%',
                                  transform: 'translate(-50%,-50%) rotate(-45deg)',
                                  width: '80%',
                                  height: '1px',
                                  background: 'rgba(255,255,255,0.2)',
                                  pointerEvents: 'none',
                                }} />
                              )}
                              {size}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Price & Add to Cart - sticky on mobile */}
            <div
              className="product-add-to-cart-section"
              style={{
                background: 'rgba(0, 0, 0, 0.6)',
                padding: '24px',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}
            >
              <div style={{ marginBottom: '20px' }}>
                {(displayPrice.onSale && saleInfo?.label) && (
                  <span style={{
                    display: 'inline-block',
                    padding: '4px 10px',
                    background: 'rgba(16, 185, 129, 0.2)',
                    color: '#10b981',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: '600',
                    marginBottom: '8px'
                  }}>
                    {saleInfo.label}
                  </span>
                )}
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
                  <p style={{
                    color: (product.subscription || product.type === 'subscription') ? '#c4b5fd' : '#10b981',
                    fontSize: '32px',
                    fontWeight: 'bold',
                    margin: 0
                  }}>
                    {displayPrice.priceRange
                      ? `${formatPrice(displayPrice.priceRange[0])} - ${formatPrice(displayPrice.priceRange[1])}`
                      : formatPrice(displayPrice.price)
                    }
                  </p>
                  {displayPrice.onSale && displayPrice.original != null && (
                    <span style={{ textDecoration: 'line-through', color: '#9ca3af', fontSize: '18px' }}>
                      {formatPrice(displayPrice.original)}
                    </span>
                  )}
                  {(() => {
                    // For service products (coaching), use remainingSpots (calculated from maxClients - purchases)
                    // For physical products, use inventoryQty
                    const spotsAvailable = product.productType === 'service' || product.type === 'service'
                      ? (product.remainingSpots !== undefined && product.remainingSpots !== null ? product.remainingSpots : product.maxClients)
                      : product.inventoryQty;
                    
                    // Only show if there are spots available (greater than 0)
                    if (spotsAvailable !== undefined && spotsAvailable !== null && spotsAvailable > 0) {
                      return (
                        <span style={{
                          color: '#60a5fa',
                          fontSize: '18px',
                          fontWeight: '500',
                          marginLeft: '12px'
                        }}>
                          {spotsAvailable === 1 ? '1 spot left' : `${spotsAvailable} spots available`}
                        </span>
                      );
                    }
                    return null;
                  })()}
                  {(product.subscription || product.type === 'subscription') && (
                    <span style={{
                      color: '#c4b5fd',
                      fontSize: '18px',
                      fontWeight: '500'
                    }}>
                      /month
                    </span>
                  )}
                  {saleInfo?.showCountdown && saleInfo?.endsAt && (() => {
                    const end = new Date(saleInfo.endsAt);
                    const now = new Date();
                    if (end <= now) return null;
                    const days = Math.ceil((end - now) / (24 * 60 * 60 * 1000));
                    return (
                      <span style={{ color: '#fbbf24', fontSize: '14px', fontWeight: '500', marginLeft: '8px' }}>
                        {days <= 1 ? 'Ends soon' : `${days} days left`}
                      </span>
                    );
                  })()}
                </div>
                <p style={{
                  color: '#9ca3af',
                  fontSize: '14px',
                  margin: '4px 0 0 0'
                }}>
                  {(product.subscription || product.type === 'subscription') 
                    ? 'Zee Mystery Subscription Test • billed monthly during the experiment'
                    : (() => {
                        const parts = [currentVariant?.color, currentVariant?.size].filter(Boolean);
                        return parts.length > 0
                          ? `${parts.join(' / ')} • ${product.size_info?.material || 'Premium Quality'}`
                          : product.size_info?.material || 'Premium Quality';
                      })()}
                </p>
              </div>

              {(product.subscription || product.type === 'subscription') && !user && (
                <div style={{
                  background: 'rgba(236, 72, 153, 0.1)',
                  border: '1px solid rgba(236, 72, 153, 0.3)',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '16px',
                  textAlign: 'center'
                }}>
                  <p style={{
                    color: '#fbcfe8',
                    fontSize: '14px',
                    margin: 0
                  }}>
                    ⚠️ You must have a profile to subscribe. Please <a href="/auth" style={{ color: '#ec4899', textDecoration: 'underline' }}>sign in or create your Rainbow Sails account</a> first.
                  </p>
                </div>
              )}

              {/* Quantity selector for non-subscription products */}
              {!(product.subscription || product.type === 'subscription') && (
                <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ color: 'white', fontSize: '14px', fontWeight: '500' }}>Quantity:</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'rgba(255,255,255,0.1)', borderRadius: '8px', padding: '4px' }}>
                    <button
                      type="button"
                      aria-label="Decrease quantity"
                      onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '6px',
                        border: '1px solid rgba(255,255,255,0.2)',
                        background: 'rgba(255,255,255,0.1)',
                        color: 'white',
                        fontSize: '18px',
                        cursor: 'pointer'
                      }}
                    >
                      −
                    </button>
                    <span style={{ color: 'white', minWidth: '28px', textAlign: 'center', fontSize: '16px', fontWeight: '600' }}>{quantity}</span>
                    <button
                      type="button"
                      aria-label="Increase quantity"
                      onClick={() => setQuantity((q) => Math.min(99, q + 1))}
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '6px',
                        border: '1px solid rgba(255,255,255,0.2)',
                        background: 'rgba(255,255,255,0.1)',
                        color: 'white',
                        fontSize: '18px',
                        cursor: 'pointer'
                      }}
                    >
                      +
                    </button>
                  </div>
                </div>
              )}

              <button
                onClick={handleAddToCart}
                disabled={subscriptionLoading || ((product.subscription || product.type === 'subscription') && !user)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px',
                  padding: '16px',
                  background: (product.subscription || product.type === 'subscription') 
                    ? 'linear-gradient(135deg, #8b5cf6, #ec4899)' 
                    : 'linear-gradient(135deg, #10b981, #059669)',
                  color: 'white',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: (subscriptionLoading || ((product.subscription || product.type === 'subscription') && !user)) ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: (product.subscription || product.type === 'subscription')
                    ? '0 4px 15px rgba(139, 92, 246, 0.5)'
                    : '0 4px 15px rgba(16, 185, 129, 0.3)',
                  opacity: (subscriptionLoading || ((product.subscription || product.type === 'subscription') && !user)) ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (!subscriptionLoading && !((product.subscription || product.type === 'subscription') && !user)) {
                    e.target.style.background = (product.subscription || product.type === 'subscription')
                      ? 'linear-gradient(135deg, #7c3aed, #db2777)'
                      : 'linear-gradient(135deg, #059669, #047857)';
                    e.target.style.transform = 'scale(1.02)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!subscriptionLoading && !((product.subscription || product.type === 'subscription') && !user)) {
                    e.target.style.background = (product.subscription || product.type === 'subscription')
                      ? 'linear-gradient(135deg, #8b5cf6, #ec4899)'
                      : 'linear-gradient(135deg, #10b981, #059669)';
                    e.target.style.transform = 'scale(1)';
                  }
                }}
              >
                <ShoppingBag size={20} />
                {subscriptionLoading 
                  ? 'Processing...' 
                  : (product.subscription || product.type === 'subscription') 
                    ? (!user ? 'Sign In to Subscribe' : 'Subscribe Now') 
                    : 'Add to Cart'}
              </button>
              
              {(product.subscription || product.type === 'subscription') && user && (
                <div style={{
                  background: 'rgba(139, 92, 246, 0.1)',
                  border: '1px solid rgba(139, 92, 246, 0.3)',
                  borderRadius: '8px',
                  padding: '12px',
                  marginTop: '12px',
                  fontSize: '12px',
                  color: '#c4b5fd',
                  textAlign: 'center'
                }}>
                  <p style={{ margin: 0 }}>
                    ⚠️ Zee\'s reminder: auto-renew starts OFF. After subscribing, open Profile &gt; Subscription and tap "Enable Auto-Renew" to stay in the $1 mystery subscription test and claim the final drop.
                  </p>
                </div>
              )}
            </div>

            {/* Product Specifications */}
            {product && (
              <div style={{
                marginTop: '32px',
                background: 'rgba(59, 130, 246, 0.1)',
                padding: '20px',
                borderRadius: '12px',
                border: '1px solid rgba(59, 130, 246, 0.2)'
              }}>
                <h4 style={{
                  color: '#3b82f6',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  marginBottom: '16px'
                }}>
                  Product Specifications
                </h4>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                  gap: '16px'
                }}>
                  {/* Extract real data from Printful API response */}
                  {product.category && (
                    <div>
                      <span style={{ color: '#9ca3af', fontSize: '14px', display: 'block' }}>Category</span>
                      <span style={{ color: 'white', fontSize: '16px', fontWeight: '500' }}>
                        {product.category}
                      </span>
                    </div>
                  )}
                  {product.technique && (
                    <div>
                      <span style={{ color: '#9ca3af', fontSize: '14px', display: 'block' }}>Technique</span>
                      <span style={{ color: 'white', fontSize: '16px', fontWeight: '500' }}>
                        {product.technique}
                      </span>
                    </div>
                  )}
                  {currentVariant?.size && (
                    <div>
                      <span style={{ color: '#9ca3af', fontSize: '14px', display: 'block' }}>Size</span>
                      <span style={{ color: 'white', fontSize: '16px', fontWeight: '500' }}>
                        {currentVariant.size}
                      </span>
                    </div>
                  )}
                  {currentVariant?.color && (
                    <div>
                      <span style={{ color: '#9ca3af', fontSize: '14px', display: 'block' }}>Color</span>
                      <span style={{ color: 'white', fontSize: '16px', fontWeight: '500' }}>
                        {currentVariant.color}
                      </span>
                    </div>
                  )}
                  {/* Extract capacity from description */}
                  {product.description && product.description.match(/\d+\s*(oz|ml|fl oz)/i) && (
                    <div>
                      <span style={{ color: '#9ca3af', fontSize: '14px', display: 'block' }}>Capacity</span>
                      <span style={{ color: 'white', fontSize: '16px', fontWeight: '500' }}>
                        {product.description.match(/\d+\s*(oz|ml|fl oz)/i)?.[0]}
                      </span>
                    </div>
                  )}
                  {/* Extract dimensions from description */}
                  {product.description && product.description.match(/Dimensions:?\s*[^•\n]+/i) && (
                    <div>
                      <span style={{ color: '#9ca3af', fontSize: '14px', display: 'block' }}>Dimensions</span>
                      <span style={{ color: 'white', fontSize: '16px', fontWeight: '500' }}>
                        {product.description.match(/Dimensions:?\s*([^•\n]+)/i)?.[1]?.trim()}
                      </span>
                    </div>
                  )}
                  {/* Extract material from description */}
                  {product.description && product.description.match(/(stainless steel|cotton|polyester|metal|plastic|wood|leather|fabric)/i) && (
                    <div>
                      <span style={{ color: '#9ca3af', fontSize: '14px', display: 'block' }}>Material</span>
                      <span style={{ color: 'white', fontSize: '16px', fontWeight: '500' }}>
                        {product.description.match(/(stainless steel|high-grade stainless steel|cotton|polyester|metal|plastic|wood|leather|fabric)/i)?.[0]}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* You might also like */}
        {relatedProducts.length > 0 && (
          <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '32px 24px' }}>
            <h2 style={{ color: 'white', fontSize: '24px', fontWeight: 'bold', marginBottom: '24px', textAlign: 'center' }}>
              You might also like
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '20px' }}>
              {relatedProducts.map((p) => {
                const img = p.primaryImage || p.customImage || (p.images?.[0]) || PLACEHOLDER_IMAGE;
                const prodSlug = p.slug || p.name?.toLowerCase().replace(/\s+/g, '-') || p.id;
                const href = `/product/${prodSlug}`;
                return (
                  <div
                    key={p.id}
                    onClick={() => navigate(href)}
                    style={{
                      background: 'rgba(0,0,0,0.4)',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      border: '1px solid rgba(255,255,255,0.1)',
                      transition: 'transform 0.2s, box-shadow 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'scale(1.02)';
                      e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'scale(1)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div style={{ aspectRatio: '1', overflow: 'hidden' }}>
                      <img src={img} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" onError={(ev) => { ev.target.src = PLACEHOLDER_IMAGE; }} />
                    </div>
                    <div style={{ padding: '12px' }}>
                      <p style={{ color: 'white', fontSize: '14px', fontWeight: '600', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</p>
                      <p style={{ color: '#10b981', fontSize: '16px', fontWeight: 'bold', margin: '4px 0 0 0' }}>
                        {p.isOnSale && p.salePrice != null ? formatPrice(p.salePrice) : formatPrice(p.price || 0)}
                        {p.isOnSale && p.originalPrice != null && <span style={{ textDecoration: 'line-through', color: '#9ca3af', fontSize: '12px', marginLeft: 6 }}>{formatPrice(p.originalPrice)}</span>}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {relatedProducts.length > 0 && (
          <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px', marginTop: '48px' }}>
            <h3 style={{ color: 'white', fontSize: '24px', fontWeight: 'bold', marginBottom: '24px' }}>You might also like</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '24px' }}>
              {relatedProducts.map((p) => {
                const imgSrc = p.primaryImage || (p.images?.[0]) || p.customImage || PLACEHOLDER_IMAGE;
                const prodSlug = p.slug || p.name?.toLowerCase().replace(/\s+/g, '-') || p.id;
                return (
                  <div
                    key={p.id}
                    onClick={() => navigate(`/product/${prodSlug}`)}
                    style={{
                      background: 'rgba(0,0,0,0.4)',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      border: '1px solid rgba(255,255,255,0.1)',
                      transition: 'transform 0.2s, box-shadow 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'scale(1.02)';
                      e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'scale(1)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <img src={imgSrc} alt={p.name} style={{ width: '100%', height: '180px', objectFit: 'cover' }} loading="lazy" />
                    <div style={{ padding: '16px' }}>
                      <p style={{ color: 'white', fontWeight: '600', margin: '0 0 8px 0' }}>{p.name}</p>
                      <p style={{ color: '#10b981', fontWeight: 'bold', margin: 0 }}>
                        {p.isOnSale && p.salePrice != null ? formatPrice(p.salePrice) : formatPrice(p.price)}
                        {p.isOnSale && p.originalPrice != null && <span style={{ textDecoration: 'line-through', color: '#9ca3af', fontSize: '12px', marginLeft: 6 }}>{formatPrice(p.originalPrice)}</span>}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Product Reviews Section */}
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
          <ProductReviews productId={product.id} productName={product.name} />
        </div>
      </div>

      {lightboxImage && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Zee full screen image viewer"
          onClick={closeLightbox}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '24px',
            zIndex: 1000,
            backdropFilter: 'blur(6px)'
          }}
        >
          <div
            onClick={(event) => event.stopPropagation()}
            style={{
              maxWidth: '95vw',
              maxHeight: '85vh',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '16px'
            }}
          >
            <button
              onClick={closeLightbox}
              style={{
                alignSelf: 'flex-end',
                background: 'rgba(255, 255, 255, 0.12)',
                color: '#f472b6',
                border: '1px solid rgba(244, 114, 182, 0.4)',
                borderRadius: '9999px',
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(event) => {
                event.currentTarget.style.background = 'rgba(244, 114, 182, 0.25)';
              }}
              onMouseLeave={(event) => {
                event.currentTarget.style.background = 'rgba(255, 255, 255, 0.12)';
              }}
            >
              Zee Close
            </button>
            <img
              src={lightboxImage}
              alt={lightboxCaption || 'Product'}
              style={{
                maxWidth: '100%',
                maxHeight: '72vh',
                objectFit: 'contain',
                borderRadius: '12px',
                boxShadow: '0 20px 50px rgba(0,0,0,0.45)',
              }}
            />
            {lightboxCaption ? (
              <p style={{ color: '#e2e8f0', margin: 0, textAlign: 'center', maxWidth: '720px' }}>
                {lightboxCaption}
              </p>
            ) : null}
          </div>
        </div>
      )}

      {/* Size Guide Modal */}
      {showSizeGuide && product?.sizeGuide && (() => {
        const guide = product.sizeGuide;
        const tables = Array.isArray(guide.size_tables) ? guide.size_tables : [];
        const activeTable = tables.find(t => t.unit === sizeGuideUnit) || tables[0];
        const otherUnit = sizeGuideUnit === 'inches' ? 'cm' : 'inches';
        const hasOtherUnit = tables.some(t => t.unit === otherUnit);

        return (
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Size Guide"
            onClick={() => setShowSizeGuide(false)}
            style={{
              position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
              background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center',
              justifyContent: 'center', zIndex: 1100, padding: '16px',
              backdropFilter: 'blur(6px)',
            }}
          >
            <div
              onClick={e => e.stopPropagation()}
              style={{
                background: '#1a1a2e', border: '1px solid rgba(139,92,246,0.3)',
                borderRadius: '16px', padding: '28px', maxWidth: '640px', width: '100%',
                maxHeight: '85vh', overflowY: 'auto',
              }}
            >
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 style={{ color: 'white', margin: 0, fontSize: '20px', fontWeight: '700' }}>
                  Size Guide
                </h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  {hasOtherUnit && (
                    <div style={{ display: 'flex', background: 'rgba(255,255,255,0.08)', borderRadius: '8px', padding: '3px' }}>
                      {['inches', 'cm'].map(u => (
                        <button
                          key={u}
                          type="button"
                          onClick={() => setSizeGuideUnit(u)}
                          style={{
                            padding: '5px 14px', borderRadius: '6px', border: 'none',
                            background: sizeGuideUnit === u ? '#8b5cf6' : 'transparent',
                            color: 'white', cursor: 'pointer', fontSize: '13px', fontWeight: '500',
                          }}
                        >
                          {u === 'inches' ? 'in' : 'cm'}
                        </button>
                      ))}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => setShowSizeGuide(false)}
                    style={{
                      background: 'rgba(255,255,255,0.1)', border: 'none', color: 'white',
                      borderRadius: '8px', width: '32px', height: '32px', cursor: 'pointer',
                      fontSize: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    ×
                  </button>
                </div>
              </div>

              {/* Disclaimer */}
              {activeTable?.description && (
                <p style={{
                  color: '#e2b96f', fontSize: '13px', background: 'rgba(234,179,8,0.1)',
                  border: '1px solid rgba(234,179,8,0.2)', borderRadius: '8px',
                  padding: '10px 14px', marginBottom: '20px', lineHeight: '1.5',
                }}>
                  {activeTable.description.replace(/<[^>]+>/g, '').trim()}
                </p>
              )}

              {/* Measurement table */}
              {activeTable?.measurements?.length > 0 && (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                    <thead>
                      <tr>
                        <th style={{
                          textAlign: 'left', padding: '10px 12px', color: '#9ca3af',
                          fontSize: '12px', fontWeight: '600', textTransform: 'uppercase',
                          letterSpacing: '0.06em', borderBottom: '1px solid rgba(255,255,255,0.1)',
                        }}>
                          {activeTable.unit === 'inches' ? 'Measurement (in)' : 'Measurement (cm)'}
                        </th>
                        {(guide.available_sizes || []).map(size => (
                          <th key={size} style={{
                            textAlign: 'center', padding: '10px 8px', color: 'white',
                            fontSize: '13px', fontWeight: '600',
                            borderBottom: '1px solid rgba(255,255,255,0.1)',
                            minWidth: '48px',
                          }}>
                            {size}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {activeTable.measurements.map((row, ri) => (
                        <tr key={ri} style={{ background: ri % 2 === 0 ? 'rgba(255,255,255,0.03)' : 'transparent' }}>
                          <td style={{
                            padding: '10px 12px', color: '#d1d5db', fontWeight: '500',
                            borderBottom: '1px solid rgba(255,255,255,0.06)',
                          }}>
                            {row.type_label}
                          </td>
                          {(guide.available_sizes || []).map(size => {
                            const val = row.values?.find(v => v.size === size);
                            const display = val
                              ? (val.min_value && val.max_value
                                  ? `${val.min_value}–${val.max_value}`
                                  : val.value || '—')
                              : '—';
                            return (
                              <td key={size} style={{
                                textAlign: 'center', padding: '10px 8px', color: 'white',
                                borderBottom: '1px solid rgba(255,255,255,0.06)',
                              }}>
                                {display}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* How-to-measure image */}
              {activeTable?.image_url && (
                <div style={{ marginTop: '20px', textAlign: 'center' }}>
                  <img
                    src={activeTable.image_url}
                    alt="How to measure"
                    style={{ maxWidth: '100%', borderRadius: '8px', opacity: 0.9 }}
                  />
                  {activeTable.image_description && (
                    <p style={{ color: '#9ca3af', fontSize: '12px', marginTop: '8px', lineHeight: '1.5' }}>
                      {activeTable.image_description.replace(/<[^>]+>/g, '').trim()}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default Product;
     